import { MouseEvent, useEffect, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, IconButton, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { RoleDto, UpdateRoleDto } from '@sr/dto';
import { roleLinkManager } from '../../../utils/link-manager';
import { Link } from 'shared/ui/link-base';
import { usePermissionsList } from './permission-picker/hook';
import { PermissionGroups } from './permission-groups';
import { useSnack } from '../../../shared/ui/snack';

type Props = {
  role: RoleDto;
  onSave: (dto: UpdateRoleDto) => Promise<void>;
  onDelete: (role: RoleDto) => Promise<void>;
};

export const RoleAccordionEditable = ({ role, onSave, onDelete }: Props) => {
  const { list: allPermissions = [] } = usePermissionsList();
  const { showSuccess, showError } = useSnack();

  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(role.title);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set((role.permissions ?? []).map((p) => p.id)));

  useEffect(() => {
    if (!editing) {
      setTitle(role.title);
      setSelectedIds(new Set((role.permissions ?? []).map((p) => p.id)));
    }
  }, [role, editing]);

  const togglePermissions = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onStartEdit = (e: MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
    setExpanded(true);
  };

  const onCancel = (e: MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
    setExpanded(false);
  };

  const onSaveClick = async (e: MouseEvent) => {
    e.stopPropagation();

    const dto: UpdateRoleDto = {
      id: role.id,
      title: title.trim(),
      permissions: Array.from(selectedIds),
    };

    try {
      await onSave(dto);
      showSuccess('Роль успешно обновлена');
      setEditing(false);
      setExpanded(false);
    } catch (err) {
      showError('Ошибка обновления роли', err);
    }
  };

  const onDeleteClick = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await onDelete(role);
      showSuccess('Роль успешно удалена');
    } catch (err) {
      showError('Ошибка удаления роли', err);
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      expanded={expanded || editing}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          {!editing ? (
            <Link to={roleLinkManager.link(role.id)} onClick={(ev: MouseEvent) => ev.stopPropagation()}>
              {role.title}
            </Link>
          ) : (
            <TextField
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Название роли"
              sx={{ minWidth: 280 }}
            />
          )}

          <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            {!editing ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  {role.permissions?.length ?? 0} пермисий
                </Typography>
                <IconButton size="small" onClick={onStartEdit} aria-label="Редактировать">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={onDeleteClick}
                  aria-label="Удалить"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton size="small" color="success" onClick={onSaveClick} aria-label="Сохранить" disabled={!title.trim()}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={onCancel} aria-label="Отменить">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails onClick={(e) => e.stopPropagation()}>
        {!editing ? (
          <PermissionGroups permissions={role.permissions ?? []} mode="view" emptyLabel="Нет пермиссий" />
        ) : (
          <PermissionGroups permissions={allPermissions} mode="edit" selectedIds={selectedIds} onToggle={togglePermissions} emptyLabel="Пермиссии не найдены" />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
